import React from 'react';
import { useStore } from 'context/store-context';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import {
  CREATE_SUBSCRIPTION_SESSION,
  GET_STORE_MONTHLY_ORDER_VOLUME,
} from 'gql/queries';
import { useMutation, useQuery } from '@apollo/client';
import { StoreReturnPricingTable } from 'components/ReturnPricingTable';

const PRICING_TIERS = {
  shipaid_plus_1k_monthly: 149,
  shipaid_plus_2k_monthly: 169,
  shipaid_plus_3k_monthly: 199,
  shipaid_plus_4k_monthly: 229,
  shipaid_plus_5k_monthly: 249,
} as const;

export const PricingSettings = ({
  activeNav = 'pricing',
}: {
  activeNav?: string;
}) => {
  const { storeId, storeProperties } = useStore();
  const currentPlan = storeProperties?.pricing;

  const { data: volumeData } = useQuery(GET_STORE_MONTHLY_ORDER_VOLUME, {
    variables: { input: { storeId } },
    skip: !storeId,
  });

  const monthlyOrderVolume = volumeData?.storeMonthlyOrderVolume || 0;

  const [selectedTier, setSelectedTier] = React.useState(
    'shipaid_plus_1k_monthly',
  );

  // Update selected tier if it's disabled
  React.useEffect(() => {
    const getLimit = (tier: string) => {
      const match = tier.match(/(\d+)k/);
      return match ? parseInt(match[1]) * 1000 : 5000;
    };

    const currentLimit = getLimit(selectedTier);
    if (
      monthlyOrderVolume > currentLimit &&
      selectedTier !== 'shipaid_plus_5k_monthly'
    ) {
      // Find the first valid tier
      const validTier = Object.keys(PRICING_TIERS).find((tier) => {
        const limit = getLimit(tier);
        return monthlyOrderVolume <= limit;
      });
      if (validTier) {
        setSelectedTier(validTier);
      } else {
        setSelectedTier('shipaid_plus_5k_monthly');
      }
    }
  }, [monthlyOrderVolume, selectedTier]);

  const [createSubscriptionSession, { loading }] = useMutation(
    CREATE_SUBSCRIPTION_SESSION,
  );

  const handleSubscribe = async (plan: string) => {
    try {
      const { data } = await createSubscriptionSession({
        variables: {
          input: {
            storeId: storeId,
            plan,
            variant: plan === 'SHIPAID_PLUS_PLAN' ? selectedTier : undefined,
            returnUrl: window.location.href,
          },
        },
      });

      if (data?.createSubscriptionSession?.url) {
        window.location.href = data.createSubscriptionSession.url;
      }
    } catch (error) {
      console.error('Error creating subscription session:', error);
      // Ideally show a toast notification here
    }
  };

  return (
    <SettingsViewWrapper activeNav={activeNav}>
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-white">
        <main className="flex-1 overflow-y-auto bg-white p-8">
          <div className="max-w-4xl mx-auto grid grid-cols-12 gap-8 pb-24">
            <div className="col-span-12 lg:col-span-12 flex flex-col gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h1 className="text-2xl font-bold text-gray-900">
                        Pricing Configuration
                      </h1>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Select the plan that best fits your business needs. Configure
                  your service tiers to offer premium Delivery Guarantee and
                  enhanced features to your customers.
                </p>
                {monthlyOrderVolume > 0 && (
                  <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm mb-4 inline-flex items-center gap-2">
                    <span className="material-symbols-outlined text-base">
                      info
                    </span>
                    <span>
                      Based on your recent volume ({monthlyOrderVolume}{' '}
                      orders/month), some lower-tier plans are unavailable.
                    </span>
                  </div>
                )}
              </div>
              <StoreReturnPricingTable storeId={storeId} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col relative">
                  <div className="p-5 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900">
                        SHIPAID
                      </h3>
                      <span className="material-symbols-outlined text-gray-400">
                        verified_user
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4 h-8">
                      A Delivery Guarantee for your shipments.
                    </p>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      $0{' '}
                      <span className="text-sm font-normal text-gray-500">
                        /mo
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-6">
                      15% of Delivery Guarantee Revenue
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Checkout/Cart Widget</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Self-service Claim Portal</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Basic Email Support</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Order Tracking Page</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Custom Notifications</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Order Editing</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Tree Planting & Social Impact</span>
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 border-t border-gray-100 mt-auto">
                    <button
                      className={`w-full ${
                        currentPlan === 'SHIPAID_PLAN'
                          ? 'bg-gray-100 text-gray-500 cursor-default'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      } transition-colors rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2`}
                      disabled={currentPlan === 'SHIPAID_PLAN' || loading}
                      onClick={() => handleSubscribe('SHIPAID_PLAN')}
                    >
                      {loading
                        ? 'Processing...'
                        : currentPlan === 'SHIPAID_PLAN'
                        ? 'Current Plan'
                        : 'Downgrade'}
                    </button>
                  </div>
                </div>
                <div className="bg-white border-2 border-[#1a1a1a] rounded-xl overflow-hidden shadow-md flex flex-col relative transform scale-[1.02] z-10">
                  <div className="absolute top-0 inset-x-0 bg-[#1a1a1a] text-white text-[10px] font-bold text-center py-1 uppercase tracking-wide">
                    Most Popular
                  </div>
                  <div className="p-5 flex-1 pt-8">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900">
                        SHIPAID PLUS
                      </h3>
                      <span className="material-symbols-outlined text-blue-600">
                        star
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4 h-8">
                      Advanced features for growing brands.
                    </p>
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      $
                      {
                        /* @ts-ignore */
                        PRICING_TIERS[selectedTier] || 149
                      }{' '}
                      <span className="text-sm font-normal text-gray-500">
                        /mo
                      </span>
                    </div>
                    <div className="mb-5">
                      <select
                        value={selectedTier}
                        onChange={(e) => setSelectedTier(e.target.value)}
                        className="w-full text-xs py-1.5 pl-2 pr-7 border-gray-300 rounded focus:border-[#1a1a1a] focus:ring-[#1a1a1a] text-gray-700"
                      >
                        <option
                          value="shipaid_plus_1k_monthly"
                          disabled={monthlyOrderVolume > 1000}
                        >
                          Up to 1,000 orders{' '}
                          {monthlyOrderVolume > 1000 ? '(Exceeds limit)' : ''}
                        </option>
                        <option
                          value="shipaid_plus_2k_monthly"
                          disabled={monthlyOrderVolume > 2000}
                        >
                          Up to 2,000 orders{' '}
                          {monthlyOrderVolume > 2000 ? '(Exceeds limit)' : ''}
                        </option>
                        <option
                          value="shipaid_plus_3k_monthly"
                          disabled={monthlyOrderVolume > 3000}
                        >
                          Up to 3,000 orders{' '}
                          {monthlyOrderVolume > 3000 ? '(Exceeds limit)' : ''}
                        </option>
                        <option
                          value="shipaid_plus_4k_monthly"
                          disabled={monthlyOrderVolume > 4000}
                        >
                          Up to 4,000 orders{' '}
                          {monthlyOrderVolume > 4000 ? '(Exceeds limit)' : ''}
                        </option>
                        <option value="shipaid_plus_5k_monthly">
                          Above 5,000 orders
                        </option>
                      </select>
                    </div>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-2 text-sm text-gray-700 font-medium">
                        <span className="material-symbols-outlined text-blue-600 text-lg shrink-0">
                          check_circle
                        </span>
                        <span>Everything in SHIPAID</span>
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 border-t border-gray-100 mt-auto">
                    <button
                      className={`w-full ${
                        currentPlan === 'SHIPAID_PLUS_PLAN'
                          ? 'bg-gray-100 text-gray-500 cursor-default border border-gray-200'
                          : 'bg-[#1a1a1a] text-white hover:bg-black'
                      } transition-colors rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2`}
                      disabled={currentPlan === 'SHIPAID_PLUS_PLAN' || loading}
                      onClick={() => handleSubscribe('SHIPAID_PLUS_PLAN')}
                    >
                      {loading
                        ? 'Processing...'
                        : currentPlan === 'SHIPAID_PLUS_PLAN'
                        ? 'Current Plan'
                        : 'Upgrade Now'}
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col relative">
                  <div className="p-5 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900">
                        Enterprise
                      </h3>
                      <span className="material-symbols-outlined text-gray-400">
                        domain
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4 h-8">
                      Custom solutions for high volume.
                    </p>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      Custom
                    </div>
                    <p className="text-xs text-gray-500 mb-6">
                      For stores &gt; 5,000 orders/mo
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-2 text-sm text-gray-700 font-medium">
                        <span className="material-symbols-outlined text-gray-400 text-lg shrink-0">
                          check_circle
                        </span>
                        <span>Everything in PLUS</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Dedicated Account Manager</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Custom API Access</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>SLA & Priority Support</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="material-symbols-outlined text-green-500 text-lg shrink-0">
                          check
                        </span>
                        <span>Custom A La Carte Features</span>
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 border-t border-gray-100 mt-auto">
                    <button
                      className={`w-full ${
                        currentPlan === 'ENTERPRICE_PLAN'
                          ? 'bg-gray-100 text-gray-500 cursor-default'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      } transition-colors rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2`}
                      disabled={currentPlan === 'ENTERPRICE_PLAN'}
                    >
                      {currentPlan === 'ENTERPRICE_PLAN'
                        ? 'Current Plan'
                        : 'Contact Sales'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </SettingsViewWrapper>
  );
};
